import { defineStore } from 'pinia';
import { cloneDeep, isFinite } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { MANUSCRIPT_V1_API_URL_PREFIX as URL_PREFIX } from '@/lib/constants/manuscripts/api';
import * as types from '@/lib/constants/manuscripts/store';
import {
  useUserStore,
} from '@/stores';

export const ERROR_INVALID_GUID = 'ErrorInvalidGuid';
export const ERROR_INVALID_POINTER_GUID = 'ErrorInvalidPointerGuid';
export const ERROR_VALIDATION_FAILED = 'ErrorValidationFailed';
export const ERROR_INVALID_FILENAME = 'ErrorInvalidFilename';
export const ERROR_INVALID_FILE_EXTENSION = 'ErrorInvalidFileExtension';
export const ERROR_FAILED_TO_IMPORT = 'ErrorFailedToImport';

export const PREVENT_IMPORT_ERRORS = [
  ERROR_INVALID_GUID,
  ERROR_INVALID_POINTER_GUID,
  ERROR_VALIDATION_FAILED,
  ERROR_INVALID_FILENAME,
  ERROR_INVALID_FILE_EXTENSION,
];

export const GUID_IMPORT_ERRORS = [
  ERROR_INVALID_GUID,
  ERROR_INVALID_POINTER_GUID,
];

export const SORT_FIELD_NAME = 'title';
export const SORT_FIELD_UPLOADED_BY = 'creator';
export const SORT_FIELD_DATE_ADDED = 'created_on';
export const SORT_FIELD_STATUS = 'import_status';

const MANUSCRIPT_UPDATE_INTERVAL = 15000;

const useManuscriptImportStore = defineStore('manuscript-import', {
  state: () => ({
    importItems: [],
    isImporting: false,
    isFetchingManuscripts: false,
    fetchManuscriptsError: null,
    manuscripts: null,
    searchText: null,
    searchParams: null,
    sortField: 'created_on',
    sortDirection: 'desc',
    pagination: null,
    guidInputText: null,
    fileImportAcceptTypes: ['.docx'],
    flexBoxes: {
      guidInput: {
        heightPct: 50,
      },
      importList: {
        heightPct: 50,
      },
    },
    manuscriptUpdatesTimeout: null,
    manuscriptUpdatesEnabled: false,
    confirmNavigateDialog: null,
  }),
  getters: {
    showImportList() {
      return !!this.importItems?.length;
    },
    flexBoxHeights() {
      return {
        guidInput: {
          heightPct: this.showImportList
            ? this.flexBoxes.guidInput.heightPct
            : 100,
        },
        importList: {
          heightPct: this.flexBoxes.importList.heightPct,
        },
      };
    },
    guidImportItems: (state) => state.importItems.filter(i => !!i.guid),
    fileImportItems: (state) => state.importItems.filter(i => !!i.file),
    canImportItems() {
      return this.hasImportItems
        && !this.isValidating
        && !this.isImporting
        && !this.hasPreventImportErrors;
    },
    hasErrors() {
      return this.importItems.some((i) => i.errors?.length);
    },
    hasGuidImportErrors() {
      return this.importItems.some((i) => {
        const foundGuidError = i.errors.find(
          (error) => GUID_IMPORT_ERRORS.includes(error),
        );
        return !!foundGuidError;
      });
    },
    hasPreventImportErrors() {
      return this.importItems.some((i) => {
        const foundPreventImportError = i.errors.find(
          (error) => PREVENT_IMPORT_ERRORS.includes(error),
        );
        return !!foundPreventImportError;
      });
    },
    canCancelImport() {
      return this.hasImportItems
        && !this.isValidating
        && !this.isImporting;
    },
    hasImportItems() {
      return !!this.importItems.length;
    },
    isValidating() {
      return this.importItems.some((i) => i.isValidating);
    },
  },
  actions: {
    [types.SET_CONFIRM_NAVIGATE_DIALOG](dialog) {
      this.confirmNavigateDialog = dialog;
    },
    [types.UPDATE_FLEX_BOXES](flexBoxes) {
      this.flexBoxes = flexBoxes;
    },
    [types.CLEAR_IMPORT_DATA]({ ids } = {}) {
      if (!ids) {
        this.importItems = [];
      } else {
        this.importItems = this.importItems.filter((i) => !ids.includes(i.id));
      }

      this.guidInputText = '';
    },
    [types.SET_MANUSCRIPTS_SEARCH_TEXT](value) {
      this.searchText = value;
    },
    [types.SET_MANUSCRIPTS_SORT]({ direction, field }) {
      this.sortField = field;
      this.sortDirection = direction;
    },
    [types.SET_GUID_IMPORT_TEXT](value) {
      this.guidInputText = value;
    },
    [types.REMOVE_IMPORT_ITEM](importItem) {
      const index = this.importItems.findIndex(i => i.id === importItem.id);
      if (index !== -1) {
        this.importItems.splice(index, 1);
      }
    },
    [types.ADD_IMPORT_ITEMS](importItems) {
      importItems.reverse().forEach((item) => {
        if (item.guid) {
          if (!this.importItems.find(i => i.guid === item.guid)) {
            this.importItems.unshift(item);
          }
        }
        if (item.file) {
          if (!this.importItems.find(i => i.file?.name === item.file.name)) {
            this.importItems.unshift(item);
          }
        }
      });
    },
    [types.UPDATE_IMPORT_ITEMS](updateItems) {
      const updatedItems = [];

      updateItems.forEach((updateItem) => {
        const itemToUpdate = this.importItems.find(i => i.id === updateItem.id);
        let updated = false;

        if (itemToUpdate) {
          Object.keys(updateItem).forEach((key) => {
            const updatedValue = updateItem[key];
            if (updatedValue !== itemToUpdate[key]) {
              itemToUpdate[key] = updatedValue;
              updated = true;
            }
          });
        }

        if (updated) {
          updatedItems.push(itemToUpdate);
        }
      });

      return updatedItems;
    },
    [types.UPDATE_AND_VALIDATE_IMPORT_ITEMS](updateItems) {
      const updatedItems = this[types.UPDATE_IMPORT_ITEMS](updateItems);
      this[types.VALIDATE_IMPORT_ITEMS](updatedItems);
    },
    [types.ADD_GUID_IMPORT_ITEMS](guids) {
      const importItems = guids.map((guid) => ({
        id: uuid(),
        guid,
        isGuid: true,
        errors: [],
        isValidating: false,
      }));

      this[types.ADD_IMPORT_ITEMS](importItems);

      this[types.VALIDATE_IMPORT_ITEMS](importItems);
    },
    [types.ADD_FILE_IMPORT_ITEMS](files) {
      const importItems = files.map((file) => ({
        id: uuid(),
        fileName: file.name,
        file,
        isFile: true,
        errors: [],
        isValidating: false,
      }));

      this[types.ADD_IMPORT_ITEMS](importItems);

      this[types.VALIDATE_IMPORT_ITEMS](importItems);
    },
    async [types.ADD_IMPORT_ITEMS_TO_QUEUE]() {
      if (!this.canImportItems) {
        return;
      }

      try {
        this.isImporting = true;

        const userStore = useUserStore();
        const guids = this.guidImportItems.map((i) => i.guid);
        const hasGuids = !!guids.length;
        const hasFiles = !!this.fileImportItems.length;
        let manuscriptsImported = false;
        let guidRequest;
        let fileRequest;

        if (hasGuids) {
          const path = `${URL_PREFIX}/imports/studio`;
          const reqBody = {
            ids: guids,
            // TODO: wire this up
            product_id: '0253faf8-c8e4-468f-afb9-10b816629965',
          };

          guidRequest = this.appApi(path, {
            method: 'POST',
            body: reqBody,
            headers: {
              'X-Token': userStore.user.token,
            },
          });
        }

        if (hasFiles) {
          const formData = new FormData();

          this.fileImportItems.forEach((item) => {
            const fileName = item.fileName.trim() || item.file.name;

            formData.append('manuscript_files', item.file, fileName);
          });

          const path = `${URL_PREFIX}/imports/document`;

          fileRequest = this.appApi(path, {
            method: 'POST',
            body: formData,
            headers: {
              'X-Token': userStore.user.token,
            },
          }, false);
        }

        const handleGuidResponse = (guidResponse) => {
          try {
            const { errored } = guidResponse?.data;

            const updateItems = this.guidImportItems
              .filter((i) => errored.includes(i.guid))
              .map((i) => ({ id: i.id, errors: [ERROR_FAILED_TO_IMPORT] }));

            this[types.UPDATE_IMPORT_ITEMS](updateItems);

            const clearIds = this.guidImportItems
              .filter((i) => !errored.includes(i.guid))
              .map((i) => i.id);

            this[types.CLEAR_IMPORT_DATA]({ ids: clearIds });

            if (clearIds.length) {
              manuscriptsImported = true;
            }
          } catch {
            const updateItems = this.guidImportItems.map(
              (i) => ({ id: i.id, errors: [ERROR_FAILED_TO_IMPORT] }),
            );
            this[types.UPDATE_IMPORT_ITEMS](updateItems);
          }
        };

        const handleFileResponse = (fileResponse) => {
          try {
            const { errored } = fileResponse?.data;

            const updateItems = this.fileImportItems
              .filter((i) => errored.includes(i.fileName))
              .map((i) => ({ id: i.id, errors: [ERROR_FAILED_TO_IMPORT] }));

            this[types.UPDATE_IMPORT_ITEMS](updateItems);

            const clearIds = this.fileImportItems
              .filter((i) => !errored.includes(i.fileName))
              .map((i) => i.id);

            this[types.CLEAR_IMPORT_DATA]({ ids: clearIds });

            if (clearIds.length) {
              manuscriptsImported = true;
            }
          } catch {
            const updateItems = this.fileImportItems.map(
              (i) => ({ id: i.id, errors: [ERROR_FAILED_TO_IMPORT] }),
            );
            this[types.UPDATE_IMPORT_ITEMS](updateItems);
          }
        };

        if (hasGuids) {
          try {
            const guidResponse = await guidRequest;
            handleGuidResponse(guidResponse);
          } catch (error) {
            handleGuidResponse(error.response);
          }
        }

        if (hasFiles) {
          try {
            const fileResponse = await fileRequest;
            handleFileResponse(fileResponse);
          } catch (error) {
            handleFileResponse(error.response);
          }
        }

        if (manuscriptsImported) {
          this.sortDirection = 'desc';
          this.sortField = SORT_FIELD_DATE_ADDED;

          await this[types.GET_IMPORTED_MANUSCRIPTS]();
        }
      } finally {
        this.isImporting = false;
      }
    },
    async [types.VALIDATE_IMPORT_ITEMS](items) {
      const guidItems = items.filter((i) => ('guid' in i));
      const fileItems = items.filter((i) => ('file' in i));

      if (fileItems.length) {
        const updateItems = fileItems.map((item) => {
          const updateItem = {
            id: item.id,
            errors: [],
          };

          const extList = this.fileImportAcceptTypes
            .map((t) => [
              `\\${t.toLocaleLowerCase()}`,
              `\\${t.toUpperCase()}`,
            ])
            .flat()
            .join('|');
          const extGroup = `(${extList})$`;
          const fileExtensionExp = new RegExp(extGroup);
          const fileNameExp = new RegExp(`^.+${extGroup}`);

          if (!fileExtensionExp.test(item.fileName)) {
            updateItem.errors.push(ERROR_INVALID_FILE_EXTENSION);
          }

          if (!fileNameExp.test(item.fileName)) {
            updateItem.errors.push(ERROR_INVALID_FILENAME);
          }

          return updateItem;
        });

        this[types.UPDATE_IMPORT_ITEMS](updateItems);
      }

      if (guidItems.length) {
        try {
          this[types.UPDATE_IMPORT_ITEMS](
            guidItems.map((i) => ({ id: i.id, isValidating: true, errors: [] })),
          );

          const userStore = useUserStore();
          const guids = guidItems.map((i) => i.guid);
          const path = `${URL_PREFIX}/imports/studio/validate`;

          const {
            data: {
              valid_ids: validIds,
              invalid_format_ids: invalidFormatIds,
            },
          } = await this.appApi(path, {
            method: 'POST',
            body: guids,
            headers: {
              'X-Token': userStore.user.token,
            },
          });

          const updateItems = guidItems.map((item) => {
            const updateItem = {
              id: item.id,
              errors: [],
            };

            if (invalidFormatIds.includes(item.guid)) {
              updateItem.errors.push(ERROR_INVALID_GUID);
            }

            if (!validIds.includes(item.guid)) {
              updateItem.errors.push(ERROR_INVALID_POINTER_GUID);
            }

            return updateItem;
          });

          this[types.UPDATE_IMPORT_ITEMS](updateItems);
        } catch {
          this[types.UPDATE_IMPORT_ITEMS](
            guidItems.map((i) => ({ id: i.id, errors: [ERROR_VALIDATION_FAILED] })),
          );
        } finally {
          this[types.UPDATE_IMPORT_ITEMS](
            guidItems.map((i) => ({ id: i.id, isValidating: false })),
          );
        }
      }
    },
    async [types.GET_IMPORTED_MANUSCRIPTS](query = {}) {
      try {
        this.isFetchingManuscripts = true;
        this.fetchManuscriptsError = null;

        const page = parseInt(query?.page, 10);

        if ('searchText' in query) {
          this.searchText = query.searchText || '';
        }

        if (query.sortField) {
          this.sortField = query.sortField;
        }

        if (['asc', 'desc'].includes(query.sortDirection)) {
          this.sortDirection = query.sortDirection;
        }

        const searchParams = {
          search_text: this.searchText || '',
          sort_direction: this.sortDirection,
          sort_field: this.sortField,
          page: isFinite(page) ? page : 1,
          per_page: 12,
          is_imported: 1,
        };

        const serverSearchParams = new URLSearchParams(cloneDeep(searchParams));

        const guidExp = /(([0-9a-fA-F]{8})-([0-9a-fA-F]{4}-){3}([0-9a-fA-F]{12}))/g;

        if (searchParams.search_text) {
          const guids = [...searchParams.search_text.matchAll(guidExp)]
            .map((match) => match[1]);

          if (guids.length) {
            serverSearchParams.set('search_text', '');

            guids.forEach((guid) => {
              serverSearchParams.append('studio_ids', guid);
            });
          }
        }

        const queryString = serverSearchParams.toString();

        const path = `${URL_PREFIX}/manuscripts/?${queryString}`;

        const { data: { results, pagination } } = await this.appApi(path);

        this.searchParams = searchParams;
        this.manuscripts = results;
        this.pagination = pagination;

        this[types.START_MANUSCRIPT_UPDATES]();
      } catch (error) {
        const serverError = error?.response?.data?.meta;
        this.fetchManuscriptsError = {
          ...serverError,
        };
      } finally {
        this.isFetchingManuscripts = false;
      }
    },
    [types.STOP_MANUSCRIPT_UPDATES]() {
      if (this.manuscriptUpdatesTimeout) {
        clearTimeout(this.manuscriptUpdatesTimeout);
        this.manuscriptUpdatesTimeout = null;
      }
    },
    [types.SET_MANUSCRIPT_UPDATES_ENABLED](isEnabled) {
      if (!isEnabled) {
        this[types.STOP_MANUSCRIPT_UPDATES]();
      }

      this.manuscriptUpdatesEnabled = isEnabled;
    },
    [types.START_MANUSCRIPT_UPDATES]() {
      this[types.STOP_MANUSCRIPT_UPDATES]();

      const shouldStartUpdates = () => !!this.manuscripts?.length && this.manuscriptUpdatesEnabled;

      if (!shouldStartUpdates()) {
        return;
      }

      this.manuscriptUpdatesTimeout = setTimeout(async () => {
        try {
          const manuscriptIds = (this.manuscripts || []).map((m) => m.manuscript_id);

          if (manuscriptIds.length) {
            const manuscriptIdPairs = manuscriptIds.map((id) => ['manuscript_ids', id]);
            const searchParams = new URLSearchParams(manuscriptIdPairs);
            searchParams.append('is_imported', 1);
            const path = `${URL_PREFIX}/manuscripts/?${searchParams.toString()}`;

            const { data: { results } } = await this.appApi(path);

            results.forEach((manuscript) => {
              const manuscriptToUpdate = this.manuscripts.find((m) => m.id === manuscript.id);
              if (manuscriptToUpdate) {
                Object.assign(manuscriptToUpdate, {
                  import_meta: manuscript.import_meta,
                  title: manuscript.title,
                });
              }
            });
          }
        } finally {
          if (shouldStartUpdates()) {
            this[types.START_MANUSCRIPT_UPDATES]();
          }
        }
      }, MANUSCRIPT_UPDATE_INTERVAL);
    },
  },
});

export default useManuscriptImportStore;
